import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

const LEVELS = ['MASTER', 'FAMILY', 'SPECIFIC'];
const DOC_TYPES = [
  'Quality Procedure',
  'Inspection Procedure',
  'Test Procedure',
  'Work Instruction',
];
const VISIBILITY_ROLES = ['admin', 'quality-manager', 'production-engineer', 'service'];
const KNOWN_SUPPLIERS = ['gpv', 'ubiqconn', 'acmepoint', 'hk_ringer'];
const SUPPLIER_LABELS = { gpv: 'GPV', ubiqconn: 'Ubiqconn', acmepoint: 'Acmepoint', hk_ringer: 'HK Ringer' };

export default function QualityNewDocument() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  const _roleRaw = user?.publicMetadata?.role || (Array.isArray(user?.publicMetadata?.roles) ? user.publicMetadata.roles[0] : user?.publicMetadata?.roles) || '';
  const role = String(_roleRaw).toLowerCase().trim();
  const supplierKey = KNOWN_SUPPLIERS.includes(role.replace(/^test-/, '')) ? role.replace(/^test-/, '') : null;
  const canContribute = ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role) || !!supplierKey;
  if (!canContribute) {
    navigate('/quality');
  }

  const [templates, setTemplates] = useState([]);
  const [masterDocs, setMasterDocs] = useState([]);
  const [familyDocs, setFamilyDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    documentNumber: '',
    title: '',
    level: 'SPECIFIC',
    documentType: 'Quality Procedure',
    description: '',
    productFamily: '',
    productNumbers: '',
    variants: '',
    suppliers: '',
    processAreas: '',
    productionSites: '',
    owner: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '',
    effectiveDate: '',
    effectiveFromSerialNumber: '',
    effectiveFromBuildOrder: '',
    appliesToFutureProduction: true,
    appliesToWip: false,
    appliesToFinishedGoods: false,
    // ISO 9001 §7.5 document control
    classification: 'Internal',
    reviewIntervalMonths: '',
    retentionPeriod: '',
    retentionNotes: '',
    controlledCopy: true,
    distributionNotes: '',
    keywords: '',
    // Suppliers contribute docs auto-scoped to all internal NI roles + their own
    // org; NI staff choose. (The backend enforces the supplier scope regardless.)
    visibilityRoles: supplierKey ? VISIBILITY_ROLES : ['admin', 'quality-manager', 'service'],
    visibilitySuppliers: supplierKey ? [supplierKey] : [],
    supplierAccess: supplierKey ? 'Specific' : 'None',
    templateId: '',
    parentMasterId: '',
    parentFamilyId: '',
  });

  const apiCall = useCallback(async (path, method = 'GET', body = null) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return res.json();
  }, [getToken]);

  useEffect(() => {
    (async () => {
      try {
        const d = await apiCall('/quality/templates');
        if (d.ok) {
          if (d.templates?.length === 0 && ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role)) {
            await apiCall('/quality/templates/seed', 'POST');
            const d2 = await apiCall('/quality/templates');
            if (d2.ok) setTemplates(d2.templates || []);
          } else {
            setTemplates(d.templates || []);
          }
        }
      } catch { /* ignore — templates just won't pre-fill */ }
    })();
    apiCall('/quality/documents?status=Released&level=MASTER').then(d => { if (d.ok) setMasterDocs(d.documents || []); }).catch(() => {});
    apiCall('/quality/documents?status=Released&level=FAMILY').then(d => { if (d.ok) setFamilyDocs(d.documents || []); }).catch(() => {});
  }, [apiCall, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleVisRole = (r) => set('visibilityRoles',
    form.visibilityRoles.includes(r) ? form.visibilityRoles.filter(x => x !== r) : [...form.visibilityRoles, r]
  );

  const toggleVisSupplier = (s) => set('visibilitySuppliers',
    form.visibilitySuppliers.includes(s) ? form.visibilitySuppliers.filter(x => x !== s) : [...form.visibilitySuppliers, s]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.documentNumber.trim()) return setError('Document number is required');
    if (!form.title.trim()) return setError('Title is required');

    setSaving(true);
    try {
      const token = await getToken();
      const csv = (val) => val ? val.split(',').map(s => s.trim()).filter(Boolean) : [];

      const body = {
        documentNumber: form.documentNumber.trim(),
        title: form.title.trim(),
        level: form.level,
        documentType: form.documentType,
        description: form.description,
        productFamily: form.productFamily || null,
        productNumbers: csv(form.productNumbers),
        variants: csv(form.variants),
        suppliers: csv(form.suppliers),
        processAreas: csv(form.processAreas),
        productionSites: csv(form.productionSites),
        owner: form.owner,
        effectiveDate: form.effectiveDate || null,
        effectiveFromSerialNumber: form.effectiveFromSerialNumber || null,
        effectiveFromBuildOrder: form.effectiveFromBuildOrder || null,
        appliesToFutureProduction: form.appliesToFutureProduction,
        appliesToWip: form.appliesToWip,
        appliesToFinishedGoods: form.appliesToFinishedGoods,
        classification: form.classification,
        reviewIntervalMonths: form.reviewIntervalMonths ? Number(form.reviewIntervalMonths) : null,
        retentionPeriod: form.retentionPeriod,
        retentionNotes: form.retentionNotes,
        controlledCopy: form.controlledCopy,
        distributionNotes: form.distributionNotes,
        keywords: csv(form.keywords),
        visibility: {
          roles: form.visibilityRoles,
          suppliers: form.visibilitySuppliers,
          supplierAccess: form.supplierAccess,
        },
        parentDocumentIds: {
          master: form.parentMasterId || null,
          family: form.parentFamilyId || null,
        },
        templateId: form.templateId || null,
      };

      const res = await fetch(`${API_BASE}/quality/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.ok) {
        navigate(`/quality/documents/${data.document._id}/edit-draft`);
      } else {
        setError(data.message || data.error || 'Failed to create document');
      }
    } catch (err) {
      setError(err?.message || 'Request failed — check connection');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    background: 'var(--ni-panel-raised, #1D2522)',
    border: '1px solid var(--ni-border-soft)',
    color: 'var(--ni-text)',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--ni-text-muted)',
    marginBottom: 6,
  };

  const sectionStyle = {
    background: 'var(--ni-panel)',
    border: '1px solid var(--ni-border-soft)',
    borderRadius: 10,
    padding: '24px',
    marginBottom: 20,
  };

  const sectionTitle = (label) => (
    <div style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 20, color: 'var(--ni-text-muted)', borderBottom: '1px solid var(--ni-border-soft)', paddingBottom: 10 }}>
      {label}
    </div>
  );

  const showParents = form.level === 'FAMILY' || form.level === 'SPECIFIC';

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ marginBottom: 28 }}>
        <button
          onClick={() => navigate('/quality/documents')}
          style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 12 }}
        >
          ← Back to library
        </button>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>New Quality Document</h2>
        <p style={{ color: 'var(--ni-text-muted)', fontSize: 13, marginTop: 6 }}>
          Fill in the metadata, then you will be taken to the markdown editor to write the content.
        </p>
      </div>

      {error && (
        <div style={{ background: '#EC212422', border: '1px solid #EC212466', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#EC2124', fontSize: 13 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ── Identity ── */}
        <div style={sectionStyle}>
          {sectionTitle('Identity')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Document Number *</label>
              <input style={inputStyle} value={form.documentNumber} onChange={e => set('documentNumber', e.target.value)} placeholder="e.g. QP-NIN-001" required />
            </div>
            <div>
              <label style={labelStyle}>Document Type *</label>
              <select style={inputStyle} value={form.documentType} onChange={e => set('documentType', e.target.value)}>
                {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Title *</label>
            <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} placeholder="Descriptive document title" required />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
            <div>
              <label style={labelStyle}>Level *</label>
              <select style={inputStyle} value={form.level} onChange={e => set('level', e.target.value)}>
                {LEVELS.map(l => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Owner</label>
              <input style={inputStyle} value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="Name or team" />
            </div>
            <div>
              <label style={labelStyle}>Effective Date</label>
              <input style={inputStyle} type="date" value={form.effectiveDate} onChange={e => set('effectiveDate', e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>Description</label>
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional short description" />
          </div>
        </div>

        {/* ── Document Hierarchy ── */}
        {showParents && (
          <div style={sectionStyle}>
            {sectionTitle('Document Hierarchy')}
            <p style={{ fontSize: 12, color: 'var(--ni-text-muted)', marginTop: 0, marginBottom: 16 }}>
              Link this document to its parent(s) in the quality hierarchy. Only released documents are shown.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>Parent Master Document</label>
                <select style={inputStyle} value={form.parentMasterId} onChange={e => set('parentMasterId', e.target.value)}>
                  <option value="">None</option>
                  {masterDocs.map(d => (
                    <option key={d._id} value={d._id}>{d.documentNumber} — {d.title}</option>
                  ))}
                </select>
              </div>
              {form.level === 'SPECIFIC' && (
                <div>
                  <label style={labelStyle}>Parent Family Document</label>
                  <select style={inputStyle} value={form.parentFamilyId} onChange={e => set('parentFamilyId', e.target.value)}>
                    <option value="">None</option>
                    {familyDocs.map(d => (
                      <option key={d._id} value={d._id}>{d.documentNumber} — {d.title}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Applicability ── */}
        <div style={sectionStyle}>
          {sectionTitle('Applicability')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Product Family</label>
              <input style={inputStyle} value={form.productFamily} onChange={e => set('productFamily', e.target.value)} placeholder="e.g. WaveII" />
            </div>
            <div>
              <label style={labelStyle}>Product Numbers (comma-separated)</label>
              <input style={inputStyle} value={form.productNumbers} onChange={e => set('productNumbers', e.target.value)} placeholder="e.g. WE270, WE271" />
            </div>
            <div>
              <label style={labelStyle}>Variants (comma-separated)</label>
              <input style={inputStyle} value={form.variants} onChange={e => set('variants', e.target.value)} placeholder="e.g. EU, NO" />
            </div>
            <div>
              <label style={labelStyle}>Suppliers (comma-separated)</label>
              <input style={inputStyle} value={form.suppliers} onChange={e => set('suppliers', e.target.value)} placeholder="e.g. gpv, ubiqconn" />
            </div>
            <div>
              <label style={labelStyle}>Process Areas (comma-separated)</label>
              <input style={inputStyle} value={form.processAreas} onChange={e => set('processAreas', e.target.value)} placeholder="e.g. Assembly, Testing" />
            </div>
            <div>
              <label style={labelStyle}>Production Sites (comma-separated)</label>
              <input style={inputStyle} value={form.productionSites} onChange={e => set('productionSites', e.target.value)} placeholder="e.g. GPV-DK, GPV-TH" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
            <div>
              <label style={labelStyle}>Effective From Serial Number</label>
              <input style={inputStyle} value={form.effectiveFromSerialNumber} onChange={e => set('effectiveFromSerialNumber', e.target.value)} placeholder="e.g. AEGN2700001" />
            </div>
            <div>
              <label style={labelStyle}>Effective From Build Order</label>
              <input style={inputStyle} value={form.effectiveFromBuildOrder} onChange={e => set('effectiveFromBuildOrder', e.target.value)} placeholder="e.g. BO-2025-042" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
            {[
              ['appliesToFutureProduction', 'Future Production'],
              ['appliesToWip', 'WIP'],
              ['appliesToFinishedGoods', 'Finished Goods'],
            ].map(([key, label]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* ── Document Control (ISO 9001 §7.5) — NI governance, not shown to suppliers ── */}
        {!supplierKey && (
          <div style={sectionStyle}>
            {sectionTitle('Document Control')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>Classification</label>
                <select style={inputStyle} value={form.classification} onChange={e => set('classification', e.target.value)}>
                  {['Public', 'Internal', 'Confidential', 'Restricted'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Review Interval (months)</label>
                <input style={inputStyle} type="number" value={form.reviewIntervalMonths} onChange={e => set('reviewIntervalMonths', e.target.value)} placeholder="e.g. 24" />
              </div>
              <div>
                <label style={labelStyle}>Retention Period</label>
                <input style={inputStyle} value={form.retentionPeriod} onChange={e => set('retentionPeriod', e.target.value)} placeholder="e.g. 7 years after obsolescence" />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Keywords (comma-separated)</label>
              <input style={inputStyle} value={form.keywords} onChange={e => set('keywords', e.target.value)} placeholder="for retrieval — e.g. soldering, ESD, rework" />
            </div>
            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Distribution / Access Notes</label>
              <input style={inputStyle} value={form.distributionNotes} onChange={e => set('distributionNotes', e.target.value)} placeholder="Controlled-copy distribution, retrieval location…" />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginTop: 16 }}>
              <input type="checkbox" checked={form.controlledCopy} onChange={e => set('controlledCopy', e.target.checked)} />
              Controlled copy <span style={{ color: 'var(--ni-text-muted)' }}>— uncheck for an uncontrolled / for-information document</span>
            </label>
          </div>
        )}

        {/* ── Visibility ── */}
        <div style={sectionStyle}>
          {sectionTitle('Visibility')}
          {supplierKey ? (
            <div style={{ fontSize: 13, color: 'var(--ni-text-muted)', lineHeight: 1.6 }}>
              🔒 Access is set automatically for supplier contributions: all internal North Invent
              roles plus your own organisation (<strong style={{ color: 'var(--ni-green)' }}>{SUPPLIER_LABELS[supplierKey]}</strong>).
              North Invent reviews and releases the document — you do not choose the access roles.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Internal roles with access</label>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
                  {VISIBILITY_ROLES.map(r => (
                    <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={form.visibilityRoles.includes(r)} onChange={() => toggleVisRole(r)} />
                      {r}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Supplier visibility</label>
                <select style={{ ...inputStyle, width: 'auto', maxWidth: 300 }} value={form.supplierAccess} onChange={e => set('supplierAccess', e.target.value)}>
                  <option value="None">None — internal only</option>
                  <option value="ReleasedOnly">Released only (all suppliers)</option>
                  <option value="Specific">Specific suppliers</option>
                </select>
                {form.supplierAccess !== 'None' && (
                  <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
                    {KNOWN_SUPPLIERS.map(s => (
                      <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                        <input type="checkbox" checked={form.visibilitySuppliers.includes(s)} onChange={() => toggleVisSupplier(s)} />
                        {s.toUpperCase()}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Template ── */}
        {templates.length > 0 && (
          <div style={sectionStyle}>
            {sectionTitle('Start from Template')}
            <p style={{ fontSize: 12, color: 'var(--ni-text-muted)', marginTop: 0, marginBottom: 12 }}>
              The template will be pre-filled with your metadata and opened in the editor.
            </p>
            <select style={{ ...inputStyle, maxWidth: 440 }} value={form.templateId} onChange={e => set('templateId', e.target.value)}>
              <option value="">Blank document</option>
              {templates.map(t => (
                <option key={t._id} value={t._id}>{t.templateName} ({t.documentLevel})</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: saving ? '#333' : 'var(--ni-green)',
              color: '#000', border: 'none', borderRadius: 6,
              padding: '10px 24px', fontWeight: 700, fontSize: 14,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Creating...' : 'Create & Open Editor'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/quality/documents')}
            style={{ background: 'none', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text-muted)', borderRadius: 6, padding: '10px 20px', cursor: 'pointer', fontSize: 14 }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
